import React, { useState, useEffect, useMemo } from "react";
import {
  collection,
  addDoc,
  getDocs,
  updateDoc,
  doc,
  Timestamp,
} from "firebase/firestore";
import { db } from "../utils/firebase";
import { useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet";
import {
  ListRestart,
  X,
  Info,
  Search,
  Home,
  FileText,
  Calendar,
} from "lucide-react";
import { Spinner, HouseIcon } from "../components";
import { useAuth } from "../hooks/AuthenticationProvider";

const Alumni = () => {
  const navigate = useNavigate();
  const { organizationId, organizationData, checkPermission, isLoading } =
    useAuth();
  const [houseFilter, setHouseFilter] = useState("All");
  const [searchInput, setSearchInput] = useState("");
  const [reinstateOption, setReinstateOption] = useState("resident");
  const [isReinstateModalOpen, setIsReinstateModalOpen] = useState(false);
  const [selectedResident, setSelectedResident] = useState(null);
  const [isRecordEventModalOpen, setIsRecordEventModalOpen] = useState(false);
  const [activeTab, setActiveTab] = useState("relapse"); // 'relapse' or 'rearrest'
  const [selectedAlumni, setSelectedAlumni] = useState([]);
  const [eventDate, setEventDate] = useState("");
  const [eventNotes, setEventNotes] = useState("");
  const [isSubmittingEvent, setIsSubmittingEvent] = useState(false);
  const [editedResident, setEditedResident] = useState(null);
  const [alumni, setAlumni] = useState([]);
  const [isLoadingAlumni, setIsLoadingAlumni] = useState(true);

  useEffect(() => {
    const fetchAlumni = async () => {
      if (!organizationId) return;

      try {
        setIsLoadingAlumni(true);
        const alumniRef = collection(
          db,
          `organizations/${organizationId}/alumni`
        );
        const alumniSnapshot = await getDocs(alumniRef);

        const alumniData = alumniSnapshot.docs
          .map((doc) => ({
            id: doc.id,
            ...doc.data(),
          }))
          .sort((a, b) => (a.firstName || "").localeCompare(b.firstName || ""));

        setAlumni(alumniData);
      } catch (err) {
        console.error("Error fetching alumni:", err);
      } finally {
        setIsLoadingAlumni(false);
      }
    };

    fetchAlumni();
  }, [organizationId]);

  // Handle Record Event modal
  const handleOpenRecordEventModal = () => {
    setIsRecordEventModalOpen(true);
    setActiveTab("relapse");
    setSelectedAlumni([]);
    setEventDate("");
    setEventNotes("");
  };

  const handleCloseRecordEventModal = () => {
    setIsRecordEventModalOpen(false);
    setSelectedAlumni([]);
    setEventDate("");
    setEventNotes("");
  };

  const handleAlumniSelection = (alumniId) => {
    setSelectedAlumni((prev) =>
      prev.includes(alumniId)
        ? prev.filter((id) => id !== alumniId)
        : [...prev, alumniId]
    );
  };

  const handleRecordEventSubmit = async (e) => {
    e.preventDefault();
    if (!organizationId || selectedAlumni.length === 0 || !eventDate) {
      alert("Please select at least one alumni member and provide a date.");
      return;
    }

    setIsSubmittingEvent(true);

    try {
      const updatePromises = selectedAlumni.map(async (alumniId) => {
        const alumniRef = doc(
          db,
          `organizations/${organizationId}/alumni`,
          alumniId
        );

        const alumnus = alumni.find((a) => a.id === alumniId);

        // Check if alumni already has this event recorded
        const hasExistingEvent =
          activeTab === "relapse"
            ? alumnus.relapseEvents || alumnus.stillSober === false
            : alumnus.rearrestEvents || alumnus.postArrest === true;

        let updateData = {};

        if (hasExistingEvent) {
          // Remove the event record
          if (activeTab === "relapse") {
            updateData = {
              relapseEvents: null,
              relapseNotes: null,
              relapseDate: null,
              stillSober: null, // Remove the field entirely
            };
          } else if (activeTab === "rearrest") {
            updateData = {
              rearrestEvents: null,
              rearrestNotes: null,
              rearrestDate: null,
              postArrest: null, // Remove the field entirely
            };
          }
        } else {
          // Add the event record
          updateData = {
            [`${activeTab}Events`]: Timestamp.now(),
            [`${activeTab}Notes`]: eventNotes || "",
            [`${activeTab}Date`]: Timestamp.fromDate(new Date(eventDate)),
          };

          if (activeTab === "relapse") {
            updateData.stillSober = false;
          } else if (activeTab === "rearrest") {
            updateData.postArrest = true;
          }
        }

        await updateDoc(alumniRef, updateData);
      });

      await Promise.all(updatePromises);

      // Refresh alumni data
      const alumniRef = collection(
        db,
        `organizations/${organizationId}/alumni`
      );
      const alumniSnapshot = await getDocs(alumniRef);
      const alumniData = alumniSnapshot.docs
        .map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }))
        .sort((a, b) => (a.firstName || "").localeCompare(b.firstName || ""));

      setAlumni(alumniData);

      const actionText = selectedAlumni.some((id) => {
        const alumnus = alumni.find((a) => a.id === id);
        return activeTab === "relapse"
          ? alumnus.relapseEvents || alumnus.stillSober === false
          : alumnus.rearrestEvents || alumnus.postArrest === true;
      })
        ? "updated"
        : "recorded";

      alert(
        `${
          activeTab === "relapse" ? "Relapse" : "Rearrest"
        } events ${actionText} successfully for ${
          selectedAlumni.length
        } alumni member(s).`
      );
      handleCloseRecordEventModal();
    } catch (error) {
      console.error("Error recording event:", error);
      alert(`Error recording event: ${error.message}`);
    } finally {
      setIsSubmittingEvent(false);
    }
  };

  // Handle opening the reinstate modal
  const handleOpenReinstateModal = (resident) => {
    setSelectedResident(resident);
    setEditedResident({ ...resident }); // Create a copy for editing
    setIsReinstateModalOpen(true);
  };

  // Handle form field changes
  const handleFieldChange = (e) => {
    const { name, value } = e.target;
    setEditedResident((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  // Permission checks
  const canViewProfile = checkPermission("viewAlumniProfile");
  const canReinstateResidents = checkPermission("reinstateResidents");
  const canRecordEvents =
    checkPermission("recordAlumniEvents") || canReinstateResidents;
  // Filtered alumni based on search input
  const filteredAlumni = useMemo(() => {
    return alumni
      .filter(
        (a) =>
          !searchInput ||
          (a.firstName || "")
            .toLowerCase()
            .includes(searchInput.toLowerCase()) ||
          (a.lastName || "").toLowerCase().includes(searchInput.toLowerCase())
      )
      .filter((a) => houseFilter === "All" || a.house === houseFilter);
  }, [alumni, searchInput, houseFilter]);

  const calculateDuration = (startDate, endDate) => {
    startDate = new Date(startDate);
    endDate = new Date(endDate);

    let years = endDate.getFullYear() - startDate.getFullYear();
    let months = endDate.getMonth() - startDate.getMonth();
    let days = endDate.getDate() - startDate.getDate();

    if (days < 0) {
      months -= 1;
      days += new Date(endDate.getFullYear(), endDate.getMonth(), 0).getDate();
    }
    if (months < 0) {
      years -= 1;
      months += 12;
    }

    return `${years > 0 ? `${years}y ` : ""}${months > 0 ? `${months}m ` : ""}${
      days > 0 ? `${days}d` : ""
    }`.trim();
  };

  const getLengthOfStay = (intakeDate, dischargeDate) => {
    if (!intakeDate) return "0d";
    return calculateDuration(intakeDate, dischargeDate || new Date());
  };

  const extractBaseName = (fullName) => {
    // Remove any existing counter pattern like " (1)", " (2)", etc.
    const match = fullName.match(/^(.+?)\s*\(\d+\)$/);
    return match ? match[1] : fullName;
  };

  // Handle the actual reinstate submission
  const handleReinstateSubmit = async (e) => {
    e.preventDefault();
    if (!organizationId || !editedResident) return;

    try {
      // Get the base name without any counter
      const baseName = extractBaseName(
        editedResident.fullName ||
          `${editedResident.firstName} ${editedResident.lastName}`
      );

      // Determine which collection to use based on selected option
      const collectionPath =
        reinstateOption === "resident"
          ? `organizations/${organizationId}/residents`
          : `organizations/${organizationId}/waitlist`;

      // Query to get all existing documents in the target collection
      const recordsRef = collection(db, collectionPath);
      const querySnapshot = await getDocs(recordsRef);

      // Find all matching names and determine the highest counter
      let highestCounter = 0;
      querySnapshot.docs.forEach((doc) => {
        const record = doc.data();
        const fullName = record.fullName || "";

        // Check exact match or match with counter pattern
        if (fullName === baseName) {
          highestCounter = Math.max(highestCounter, 1);
        } else {
          // Check for name with counter pattern
          const match = fullName.match(
            new RegExp(`^${baseName} \\((\\d+)\\)$`)
          );
          if (match) {
            const counter = parseInt(match[1], 10);
            highestCounter = Math.max(highestCounter, counter);
          }
        }
      });

      // Determine the new full name
      const nameWithCount =
        highestCounter > 0 ? `${baseName} (${highestCounter + 1})` : baseName;

      // Process the data for adding to the target collection
      const processedData = { ...editedResident };

      // Ensure all date fields are properly converted to Firestore Timestamps
      const dateFields = [
        "dob",
        "sobrietyDate",
        "intakeDate",
        "rentDueDate",
        "dischargeDate",
      ];

      dateFields.forEach((field) => {
        if (processedData[field]) {
          // If it's already a Firestore Timestamp
          if (
            processedData[field].toDate &&
            typeof processedData[field].toDate === "function"
          ) {
            // Keep it as is
          }
          // If it's a string date
          else if (
            typeof processedData[field] === "string" &&
            processedData[field].match(/^\d{4}-\d{2}-\d{2}$/)
          ) {
            const [year, month, day] = processedData[field]
              .split("-")
              .map((num) => parseInt(num, 10));
            processedData[field] = Timestamp.fromDate(
              new Date(year, month - 1, day, 12, 0, 0, 0)
            );
          }
          // If it's a Date object
          else if (processedData[field] instanceof Date) {
            processedData[field] = Timestamp.fromDate(processedData[field]);
          }
        }
      });

      // Set up the final data object to be stored
      const newData = {
        ...processedData,
        fullName: nameWithCount,
        dateAdded: Timestamp.now(),
      };

      if (reinstateOption === "resident") {
        // Set specific resident properties
        newData.currentResident = true;

        // Remove discharge-related fields
        delete newData.dischargeStatus;
        delete newData.dischargeDate;
        delete newData.dischargeNotes;
        delete newData.returnability;
        delete newData.sobrietyStatus;
      } else {
        // Set waitlist-specific properties
        newData.status = "pending-bed"; // This is critical for waitlist functionality
        newData.isWaitlist = true;
        newData.notes = `Reinstated from alumni on ${new Date().toLocaleDateString()}`;

        // Ensure date fields are properly set
        if (!newData.intakeDate) {
          const futureDate = new Date();
          futureDate.setDate(futureDate.getDate() + 30);
          newData.intakeDate = Timestamp.fromDate(futureDate);
        }

        if (!newData.rentDueDate) {
          newData.rentDueDate = null;
        }

        // Set default values for waitlist
        newData.balance = 0;
        newData.rentAmount = 0;

        // Remove resident-specific fields
        delete newData.currentResident;
        delete newData.dischargeStatus;
        delete newData.dischargeDate;
        delete newData.dischargeNotes;
        delete newData.returnability;
        delete newData.sobrietyStatus;
      }

      // Add the new document to the appropriate collection
      await addDoc(recordsRef, newData);

      // Refresh alumni list
      const alumniRef = collection(
        db,
        `organizations/${organizationId}/alumni`
      );
      const alumniSnapshot = await getDocs(alumniRef);

      const alumniData = alumniSnapshot.docs
        .map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }))
        .sort((a, b) => (a.firstName || "").localeCompare(b.firstName || ""));

      setAlumni(alumniData);

      // Close modal and reset state
      setIsReinstateModalOpen(false);
      setSelectedResident(null);
      setEditedResident(null);
      setReinstateOption("resident"); // Reset the option for next time

      // Show success message
      alert(
        reinstateOption === "resident"
          ? "Resident successfully reinstated!"
          : "Resident successfully added to waitlist!"
      );

      // Navigate to the appropriate page
      if (reinstateOption === "waitlist") {
        navigate("/waitlist");
      }
    } catch (err) {
      console.error("Error reinstating resident:", err);
      alert(`Error: ${err.message}`);
    }
  };

  // Dynamic House Colors (same logic as Residents.jsx)
  const getHouseColors = (house) => {
    const houseData = organizationData?.housesData?.[house];
    if (!houseData) {
      return {
        style: {
          background: `linear-gradient(to bottom, #3B82F633, rgba(15, 23, 42, 0.5))`,
          borderColor: `#3B82F64D`,
          transition: "all 0.5s",
        },
      };
    }

    const color = houseData.color;
    return {
      style: {
        background: `linear-gradient(to bottom, ${color}33, rgba(15, 23, 42, 0.5))`,
        borderColor: `${color}4D`,
        transition: "all 0.5s",
      },
    };
  };

  if (isLoading)
    return (
      <div className="flex items-center justify-center h-[80vh]">
        <Spinner />
      </div>
    );

  if (!organizationId) {
    navigate("/login");
    return null;
  }

  return (
    <>
      <Helmet>
        <title>&bull; Alumni</title>
      </Helmet>

      <div className="min-h-screen bg-[#18181B] bg-gradient-to-br from-zinc-900 via-zinc-900/95 to-zinc-950 p-6 relative overflow-hidden">
        {/* Ambient background effects */}
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_50%,rgba(24,24,27,0.3),rgba(0,0,0,0))]" />
        <div className="absolute inset-0">
          <div className="absolute top-0 -left-4 w-96 h-96 bg-cyan-500/10 rounded-full mix-blend-screen filter blur-3xl animate-pulse" />
          <div className="absolute bottom-0 right-0 w-96 h-96 bg-blue-500/10 rounded-full mix-blend-screen filter blur-3xl animate-pulse delay-700" />
        </div>

        <div className="max-w-7xl mx-auto space-y-8 relative">
          {/* Header */}
          <div className="flex items-center gap-6 mb-12">
            <div className="flex gap-3">
              <div className="h-16 w-1 bg-gradient-to-b from-cyan-500 to-cyan-600 rounded-full animate-pulse" />
              <div className="h-16 w-1 bg-gradient-to-b from-cyan-500 to-cyan-600 rounded-full animate-pulse delay-100 opacity-70" />
              <div className="h-16 w-1 bg-gradient-to-b from-cyan-500 to-cyan-600 rounded-full animate-pulse delay-200 opacity-40" />
            </div>
            <div>
              <h1 className="text-4xl font-bold text-white tracking-tight">
                Alumni Management
              </h1>
            </div>
          </div>

          <div className="relative bg-slate-900/50 backdrop-blur-xl rounded-xl overflow-hidden p-4 mb-6">
            <div className="flex flex-col md:flex-row gap-4 items-center justify-between">
              <div className="relative flex-1 max-w-md">
                <Search
                  className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400"
                  size={20}
                />
                <input
                  type="text"
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  className="flex-1 w-full pl-10 pr-3 py-2 bg-slate-800/50 text-white rounded-xl border border-slate-700 focus:border-blue-500 transition-colors"
                  placeholder="Search alumni..."
                />
              </div>

              <div className="flex gap-4 items-center">
                <select
                  value={houseFilter}
                  onChange={(e) => setHouseFilter(e.target.value)}
                  className="px-4 py-2 pr-8 bg-slate-800/50 text-white rounded-xl border border-slate-700 focus:border-blue-500 transition-colors"
                >
                  <option value="All">All Houses</option>
                  {Object.keys(organizationData?.housesData || {}).map(
                    (house) => (
                      <option key={house} value={house}>
                        {house}
                      </option>
                    )
                  )}
                </select>

                {/* Record Event Button */}
                {canRecordEvents && (
                  <button
                    onClick={handleOpenRecordEventModal}
                    className="flex items-center gap-2 px-4 py-2 bg-cyan-500/80 hover:bg-cyan-500 text-white rounded-xl transition-colors shadow-lg"
                  >
                    <FileText size={20} />
                    <span>Record Event</span>
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* Alumni Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-4 gap-4">
            {filteredAlumni.map((a) => {
              const timeInHouse = getLengthOfStay(
                a.intakeDate,
                a.dischargeDate
              );

              return (
                <div
                  key={a.id}
                  style={getHouseColors(a.house).style}
                  className="group relative backdrop-blur-xl rounded-lg overflow-hidden hover:shadow-2xl border"
                >
                  <div className="p-4 flex flex-col gap-3">
                    {/* Header with name and house icon */}
                    <div className="flex items-center justify-between mb-1">
                      <h3 className="text-lg font-semibold text-white truncate max-w-[70%]">
                        {a.fullName}
                      </h3>
                      <HouseIcon resident={a} />
                    </div>

                    {/* Stats container */}
                    <div className="grid grid-cols-1 gap-2">
                      <div className="flex items-center gap-2 bg-slate-800/30 rounded-lg p-2">
                        <Home size={16} className="text-blue-400 shrink-0" />
                        <span className="text-slate-300 text-sm font-medium truncate">
                          {timeInHouse || "0d"}
                        </span>
                      </div>
                    </div>

                    {/* Action buttons */}
                    <div className="flex gap-2 mt-1">
                      {canViewProfile && (
                        <a
                          href={`/alumni/${a.id}`}
                          className="flex-1 flex items-center justify-center gap-1.5 px-3 py-1.5 bg-blue-500/30 hover:bg-blue-500/60 text-blue-300 hover:text-blue-200 rounded-lg transition-colors text-sm shadow-sm"
                        >
                          <Info size={16} className="shrink-0" />
                          <span>Profile</span>
                        </a>
                      )}
                      {canReinstateResidents && (
                        <button
                          onClick={() => handleOpenReinstateModal(a)} // Changed from reinstateResident(a.id)
                          className="flex-1 flex items-center justify-center gap-1.5 px-3 py-1.5 bg-red-500/30 hover:bg-red-500/60 text-red-400 hover:text-red-300 rounded-lg transition-colors text-sm"
                        >
                          <ListRestart size={16} className="shrink-0" />
                          <span>Reinstate</span>
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
        {isReinstateModalOpen && editedResident && (
          <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm z-50 flex justify-center items-center">
            <div className="bg-slate-900/90 border border-slate-700 rounded-xl shadow-lg w-full max-w-4xl max-h-[90vh] overflow-y-auto">
              <div className="px-6 py-4 border-b border-slate-700 flex justify-between items-center">
                <h3 className="text-xl font-semibold text-white">
                  Reinstate Resident
                </h3>
                <button
                  onClick={() => setIsReinstateModalOpen(false)}
                  className="text-slate-400 hover:text-white transition-colors"
                >
                  <X size={24} />
                </button>
              </div>

              <form onSubmit={handleReinstateSubmit} className="p-6 space-y-6">
                {/* New Reinstate Options Section */}
                <div className="mb-6 p-4 bg-slate-800/30 rounded-lg">
                  <h4 className="text-lg font-medium text-white mb-3">
                    Reinstate Options
                  </h4>
                  <div className="flex gap-6">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        name="reinstateOption"
                        value="resident"
                        checked={reinstateOption === "resident"}
                        onChange={() => setReinstateOption("resident")}
                        className="form-radio text-blue-500"
                      />
                      <span className="text-white">Current Resident</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        name="reinstateOption"
                        value="waitlist"
                        checked={reinstateOption === "waitlist"}
                        onChange={() => setReinstateOption("waitlist")}
                        className="form-radio text-blue-500"
                      />
                      <span className="text-white">Add to Waitlist</span>
                    </label>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {/* Personal Information */}
                  <div className="space-y-4">
                    <h4 className="text-lg font-medium text-white">
                      Personal Information
                    </h4>
                    <input
                      type="text"
                      name="firstName"
                      value={editedResident.firstName}
                      onChange={handleFieldChange}
                      className="w-full px-4 py-2 bg-slate-800/50 border border-slate-700 rounded-lg text-white"
                      placeholder="First Name"
                    />
                    <input
                      type="text"
                      name="lastName"
                      value={editedResident.lastName}
                      onChange={handleFieldChange}
                      className="w-full px-4 py-2 bg-slate-800/50 border border-slate-700 rounded-lg text-white"
                      placeholder="Last Name"
                    />
                    <input
                      type="tel"
                      name="phoneNumber"
                      value={editedResident.phoneNumber}
                      onChange={handleFieldChange}
                      className="w-full px-4 py-2 bg-slate-800/50 border border-slate-700 rounded-lg text-white"
                      placeholder="Phone Number"
                    />
                    <input
                      type="date"
                      name="dob"
                      value={editedResident.dob}
                      onChange={handleFieldChange}
                      className="w-full px-4 py-2 bg-slate-800/50 border border-slate-700 rounded-lg text-white"
                    />
                  </div>

                  {/* House Information */}
                  <div className="space-y-4">
                    <h4 className="text-lg font-medium text-white">
                      House Information
                    </h4>
                    <select
                      name="house"
                      value={editedResident.house}
                      onChange={handleFieldChange}
                      className="w-full px-4 py-2 bg-slate-800/50 border border-slate-700 rounded-lg text-white"
                    >
                      <option value="">Select House</option>
                      {Object.keys(organizationData?.housesData || {}).map(
                        (house) => (
                          <option key={house} value={house}>
                            {house}
                          </option>
                        )
                      )}
                    </select>
                    <input
                      type="text"
                      name="bedLocation"
                      value={editedResident.bedLocation}
                      onChange={handleFieldChange}
                      className="w-full px-4 py-2 bg-slate-800/50 border border-slate-700 rounded-lg text-white"
                      placeholder="Bed Location"
                    />
                    <select
                      name="rentSchedule"
                      value={editedResident.rentSchedule}
                      onChange={handleFieldChange}
                      className="w-full px-4 py-2 bg-slate-800/50 border border-slate-700 rounded-lg text-white"
                    >
                      <option value="">Select Schedule</option>
                      <option value="weekly">Weekly</option>
                      <option value="monthly">Monthly</option>
                    </select>
                    <input
                      type="number"
                      name="rentAmount"
                      value={editedResident.rentAmount}
                      onChange={handleFieldChange}
                      className="w-full px-4 py-2 bg-slate-800/50 border border-slate-700 rounded-lg text-white"
                      placeholder="Rent Amount"
                    />
                    <input
                      type="number"
                      name="balance"
                      value={editedResident.balance}
                      onChange={handleFieldChange}
                      className="w-full px-4 py-2 bg-slate-800/50 border border-slate-700 rounded-lg text-white"
                      placeholder="Balance"
                    />
                    <input
                      type="date"
                      name="rentDueDate"
                      value={editedResident.rentDueDate}
                      onChange={handleFieldChange}
                      className="w-full px-4 py-2 bg-slate-800/50 border border-slate-700 rounded-lg text-white"
                    />
                  </div>

                  {/* Emergency Contact */}
                  <div className="space-y-4">
                    <h4 className="text-lg font-medium text-white">
                      Emergency Contact
                    </h4>
                    <input
                      type="text"
                      name="ecFirstName"
                      value={editedResident.ecFirstName}
                      onChange={handleFieldChange}
                      className="w-full px-4 py-2 bg-slate-800/50 border border-slate-700 rounded-lg text-white"
                      placeholder="EC First Name"
                    />
                    <input
                      type="text"
                      name="ecLastName"
                      value={editedResident.ecLastName}
                      onChange={handleFieldChange}
                      className="w-full px-4 py-2 bg-slate-800/50 border border-slate-700 rounded-lg text-white"
                      placeholder="EC Last Name"
                    />
                    <input
                      type="text"
                      name="ecRelationship"
                      value={editedResident.ecRelationship}
                      onChange={handleFieldChange}
                      className="w-full px-4 py-2 bg-slate-800/50 border border-slate-700 rounded-lg text-white"
                      placeholder="EC Relationship"
                    />
                    <input
                      type="tel"
                      name="ecPhoneNumber"
                      value={editedResident.ecPhoneNumber}
                      onChange={handleFieldChange}
                      className="w-full px-4 py-2 bg-slate-800/50 border border-slate-700 rounded-lg text-white"
                      placeholder="EC Phone Number"
                    />
                  </div>

                  {/* Legal Information */}
                  <div className="space-y-4">
                    <h4 className="text-lg font-medium text-white">
                      Legal Information
                    </h4>
                    <input
                      type="text"
                      name="poFirstName"
                      value={editedResident.poFirstName}
                      onChange={handleFieldChange}
                      className="w-full px-4 py-2 bg-slate-800/50 border border-slate-700 rounded-lg text-white"
                      placeholder="PO First Name"
                    />
                    <input
                      type="text"
                      name="poLastName"
                      value={editedResident.poLastName}
                      onChange={handleFieldChange}
                      className="w-full px-4 py-2 bg-slate-800/50 border border-slate-700 rounded-lg text-white"
                      placeholder="PO Last Name"
                    />
                    <input
                      type="text"
                      name="county"
                      value={editedResident.county}
                      onChange={handleFieldChange}
                      className="w-full px-4 py-2 bg-slate-800/50 border border-slate-700 rounded-lg text-white"
                      placeholder="County"
                    />
                    <input
                      type="text"
                      name="pendingCharges"
                      value={editedResident.pendingCharges}
                      onChange={handleFieldChange}
                      className="w-full px-4 py-2 bg-slate-800/50 border border-slate-700 rounded-lg text-white"
                      placeholder="Pending Charges"
                    />
                  </div>

                  {/* Recovery Information */}
                  <div className="space-y-4">
                    <h4 className="text-lg font-medium text-white">
                      Recovery Information
                    </h4>
                    <input
                      type="date"
                      name="sobrietyDate"
                      value={editedResident.sobrietyDate}
                      onChange={handleFieldChange}
                      className="w-full px-4 py-2 bg-slate-800/50 border border-slate-700 rounded-lg text-white"
                    />
                    <input
                      type="text"
                      name="referralLocation"
                      value={editedResident.referralLocation}
                      onChange={handleFieldChange}
                      className="w-full px-4 py-2 bg-slate-800/50 border border-slate-700 rounded-lg text-white"
                      placeholder="Referral Location"
                    />
                    <div className="flex items-center gap-2">
                      <label className="text-white">MAT Program:</label>
                      <input
                        type="checkbox"
                        name="isMAT"
                        checked={editedResident.isMAT}
                        onChange={(e) =>
                          handleFieldChange({
                            target: {
                              name: "isMAT",
                              value: e.target.checked,
                            },
                          })
                        }
                        className="form-checkbox text-emerald-500"
                      />
                    </div>
                  </div>

                  {/* UA Information */}
                  <div className="space-y-4">
                    <h4 className="text-lg font-medium text-white">
                      UA Information
                    </h4>
                    <input
                      type="number"
                      name="uaCount"
                      value={editedResident.uaCount}
                      onChange={handleFieldChange}
                      className="w-full px-4 py-2 bg-slate-800/50 border border-slate-700 rounded-lg text-white"
                      placeholder="UA Count"
                    />
                  </div>
                </div>

                <div className="flex justify-end gap-4 mt-6">
                  <button
                    type="button"
                    onClick={() => setIsReinstateModalOpen(false)}
                    className="px-4 py-2 text-sm font-medium text-slate-300 hover:text-white bg-slate-800/50 hover:bg-slate-800/70 rounded-lg border border-slate-700 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 text-sm font-medium text-white bg-emerald-500/80 hover:bg-emerald-500 rounded-lg transition-colors"
                  >
                    {reinstateOption === "resident"
                      ? "Reinstate as Resident"
                      : "Add to Waitlist"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
        {/* Record Event Modal */}
        {isRecordEventModalOpen && (
          <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm z-50 flex justify-center items-center">
            <div className="bg-slate-900/95 border border-slate-700 rounded-xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden">
              <div className="px-6 py-4 border-b border-slate-700 flex justify-between items-center">
                <h3 className="text-xl font-semibold text-white">
                  Record Alumni Event
                </h3>
                <button
                  onClick={handleCloseRecordEventModal}
                  className="text-slate-400 hover:text-white transition-colors"
                >
                  <X size={24} />
                </button>
              </div>

              {/* Tab Navigation */}
              <div className="flex border-b border-slate-700">
                <button
                  onClick={() => setActiveTab("relapse")}
                  className={`flex-1 px-6 py-3 text-sm font-medium transition-colors ${
                    activeTab === "relapse"
                      ? "text-white bg-slate-800/50 border-b-2 border-cyan-500"
                      : "text-slate-400 hover:text-white"
                  }`}
                >
                  Record Relapse
                </button>
                <button
                  onClick={() => setActiveTab("rearrest")}
                  className={`flex-1 px-6 py-3 text-sm font-medium transition-colors ${
                    activeTab === "rearrest"
                      ? "text-white bg-slate-800/50 border-b-2 border-cyan-500"
                      : "text-slate-400 hover:text-white"
                  }`}
                >
                  Record Rearrest
                </button>
              </div>

              <form
                onSubmit={handleRecordEventSubmit}
                className="p-6 space-y-6 overflow-y-auto max-h-[calc(90vh-140px)]"
              >
                {/* Alumni Selection */}
                <div className="space-y-4">
                  <h4 className="text-lg font-medium text-white">
                    Select Alumni (
                    {activeTab === "relapse" ? "Relapse" : "Rearrest"} Event)
                  </h4>

                  <div className="bg-slate-800/30 rounded-lg p-4 max-h-60 overflow-y-auto">
                    {alumni
                      .filter((alumnus) => {
                        // For relapse tab, only show alumni with sobrietyStatus of 'Sober'
                        if (activeTab === "relapse") {
                          return alumnus.sobrietyStatus === "Sober";
                        }
                        // For rearrest tab, show all alumni
                        return true;
                      })
                      .map((alumnus) => {
                        // Check if alumni already has this type of event recorded
                        const hasExistingEvent =
                          activeTab === "relapse"
                            ? alumnus.relapseEvents ||
                              alumnus.stillSober === false
                            : alumnus.rearrestEvents ||
                              alumnus.postArrest === true;

                        return (
                          <label
                            key={alumnus.id}
                            className={`flex items-center gap-3 p-2 hover:bg-slate-700/30 rounded-lg cursor-pointer ${
                              hasExistingEvent ? "opacity-75" : ""
                            }`}
                          >
                            <input
                              type="checkbox"
                              checked={selectedAlumni.includes(alumnus.id)}
                              onChange={() => handleAlumniSelection(alumnus.id)}
                              className="form-checkbox h-4 w-4 text-cyan-500 rounded border-slate-600 bg-slate-800"
                            />
                            <div className="flex items-center gap-2 flex-1">
                              <span
                                className={`text-white font-medium ${
                                  hasExistingEvent
                                    ? "line-through text-slate-400"
                                    : ""
                                }`}
                              >
                                {alumnus.fullName ||
                                  `${alumnus.firstName} ${alumnus.lastName}`}
                              </span>
                              {hasExistingEvent && (
                                <span className="text-xs px-2 py-1 bg-red-500/20 text-red-400 rounded-full">
                                  {activeTab === "relapse"
                                    ? "Relapsed"
                                    : "Rearrested"}
                                </span>
                              )}
                              {alumnus.house && (
                                <span className="text-slate-400 text-sm">
                                  ({alumnus.house})
                                </span>
                              )}
                            </div>
                          </label>
                        );
                      })}

                    {/* Show message if no eligible alumni found */}
                    {activeTab === "relapse" &&
                      alumni.filter((a) => a.sobrietyStatus === "Sober")
                        .length === 0 && (
                        <div className="text-center py-8 text-slate-400">
                          <p className="text-sm">
                            No alumni with 'Sober' status found.
                          </p>
                          <p className="text-xs mt-1">
                            Only alumni marked as 'Sober' can have relapse
                            events recorded.
                          </p>
                        </div>
                      )}

                    {activeTab === "rearrest" && alumni.length === 0 && (
                      <div className="text-center py-8 text-slate-400">
                        <p className="text-sm">No alumni found.</p>
                      </div>
                    )}
                  </div>
                  {selectedAlumni.length > 0 && (
                    <div className="text-sm space-y-1">
                      <p className="text-slate-300">
                        {selectedAlumni.length} alumni member(s) selected
                      </p>
                      {selectedAlumni.some((id) => {
                        const alumnus = alumni.find((a) => a.id === id);
                        return activeTab === "relapse"
                          ? alumnus.relapseEvents ||
                              alumnus.stillSober === false
                          : alumnus.rearrestEvents ||
                              alumnus.postArrest === true;
                      }) && (
                        <p className="text-amber-400 text-xs">
                          ⚠️ Some selected alumni already have {activeTab}{" "}
                          events recorded. Submitting will remove their existing{" "}
                          {activeTab} records.
                        </p>
                      )}
                    </div>
                  )}
                </div>

                {/* Event Details */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-4">
                    <label className="block text-sm font-medium text-white">
                      Event Date *
                    </label>
                    <div className="relative">
                      <Calendar
                        className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400"
                        size={20}
                      />
                      <input
                        type="date"
                        value={eventDate}
                        onChange={(e) => setEventDate(e.target.value)}
                        required
                        className="w-full pl-10 pr-4 py-3 bg-slate-800/50 border border-slate-700 rounded-lg text-white focus:border-cyan-500 transition-colors"
                      />
                    </div>
                  </div>
                </div>

                <div className="space-y-4">
                  <label className="block text-sm font-medium text-white">
                    Notes
                  </label>
                  <textarea
                    value={eventNotes}
                    onChange={(e) => setEventNotes(e.target.value)}
                    rows={4}
                    className="w-full px-4 py-3 bg-slate-800/50 border border-slate-700 rounded-lg text-white focus:border-cyan-500 transition-colors resize-none"
                    placeholder={`Additional notes about the ${activeTab} event...`}
                  />
                </div>
                <div className="flex justify-end gap-4 pt-4 border-t border-slate-700">
                  <button
                    type="button"
                    onClick={handleCloseRecordEventModal}
                    className="px-6 py-2 text-sm font-medium text-slate-300 hover:text-white bg-slate-800/50 hover:bg-slate-800/70 rounded-lg border border-slate-700 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={
                      isSubmittingEvent ||
                      selectedAlumni.length === 0 ||
                      !eventDate
                    }
                    className="px-6 py-2 text-sm font-medium text-white bg-cyan-500/80 hover:bg-cyan-500 disabled:bg-slate-600 disabled:cursor-not-allowed rounded-lg transition-colors flex items-center gap-2"
                  >
                    {isSubmittingEvent && <Spinner size="sm" />}
                    Record {activeTab === "relapse" ? "Relapse" : "Rearrest"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default Alumni;
